/**
 * @fileoverview Main JavaScript file for handling UI interactions and behaviors
 * on the BigPlanet website.
 *
 * @title BigPlanet Main JavaScript
 * @author Oscar Sixtos
 */

// ============================================================================
// === Constants and Variables
// ============================================================================

const topContacto = document.getElementById('top-contacto');
const navbarLogo = document.getElementById('navbar-logo');
const cookieDisclaimer = document.getElementById('cookie-disclaimer-overlay');
const cookieDisclaimerBtn = document.getElementById('cookie-disclaimer-btn');
let lastScrollY = window.scrollY;
const scrollThreshold = 10;

// ============================================================================
// === Functions
// ============================================================================

/**
 * Debounces a function to limit the rate at which it is executed.
 *
 * @function debounce
 * @param {Function} callback - The function to debounce.
 * @param {number} delay - The delay in milliseconds for debouncing.
 * @returns {Function} A debounced version of the provided function.
 */
function debounce(callback, delay) {
	let timeout;
	return function () {
		const executionContext = this;
		const args = arguments;
		clearTimeout(timeout);
		timeout = setTimeout(function () {
			callback.apply(executionContext, args);
		}, delay);
	};
}

/**
 * Resizes the navbar and topContacto element based on the scroll direction.
 *
 * @function resizeNavbarOnScroll
 */
function resizeNavbarOnScroll() {
	let currentScrollY = window.scrollY;

	// Check if the scroll position is greater than the threshold
	if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
		lastScrollY = currentScrollY;
		return; // Ignore small scrolls
	}

	if (currentScrollY > lastScrollY) {
		// Scrolling down
		topContacto.style.padding = '0px 8px';
		navbarLogo.style.height = '45px';
	} else {
		// Scrolling up
		topContacto.style.padding = '8px';
		navbarLogo.style.height = '90px';
	}

	setTimeout(() => {
		lastScrollY = window.scrollY;
	}, 510);
}

/**
 * Scrolls to the target element, accounting for the header height, 
 * when an anchor link is clicked.
 *
 * @function scrollToElementWithHeaderOffset
 * @param {Event} event - The click event triggered by the anchor link.
 */
function scrollToElementWithHeaderOffset(event) {
	event.preventDefault();
	const targetId = event.currentTarget.getAttribute('href').substring(1);
	const targetElement = document.getElementById(targetId);
	const headerHeight = document.querySelector('header').offsetHeight;

	if (targetElement) {
		const targetPosition = targetElement.offsetTop - headerHeight - 16;
		window.scrollTo({
			top: targetPosition,
			behavior: 'smooth',
		});
	}
}

/**
 * Adds the rel attribute with secure options to links opening in a new tab 
 * to prevent potential security vulnerabilities such as reverse tabnabbing.
 *
 * @function secureExternalLinks
 */
function secureExternalLinks() {
	const links = document.querySelectorAll('a[target="_blank"]');
	links.forEach((link) => {
		if (
			!link.hasAttribute('rel') ||
			!link.getAttribute('rel').includes('noopener noreferrer')
		) {
			link.setAttribute('rel', 'noopener noreferrer');
		}
	});
}

/**
 * Handles the visibility of the cookie disclaimer overlay 
 * based on user acceptance. Triggered when the DOMContentLoaded event is fired.
 *
 * @function handleCookieDisclaimer
 */
function handleCookieDisclaimer() {
	const cookieAccepted = localStorage.getItem('cookieAccepted');
	if (!cookieAccepted) {
		const cookieDisclaimer = document.getElementById(
			'cookie-disclaimer-overlay',
		);
		if (cookieDisclaimer) {
			cookieDisclaimer.classList.remove('d-none');
		}
	}
}

/**
 * Sets the cookie acceptance in local storage and hides the disclaimer overlay.
 * Triggered when the cookie disclaimer button is clicked.
 *
 * @function acceptCookieDisclaimer
 */
function acceptCookieDisclaimer() {
	localStorage.setItem('cookieAccepted', 'true');
	document
		.getElementById('cookie-disclaimer-overlay')
		.classList.add('d-none');
}

// ============================================================================
// === Event Listeners
// ============================================================================

/**
 * Adds a DOMContentLoaded event listener to handle the 
 * cookie disclaimer overlay. This ensures the cookie disclaimer 
 * is displayed if the user has not accepted it.
 */
document.addEventListener('DOMContentLoaded', handleCookieDisclaimer);

/**
 * Adds a DOMContentLoaded event listener to ensure
 * all links with target="_blank" are processed for security.
 */
document.addEventListener('DOMContentLoaded', secureExternalLinks);

/**
 * Adds a scroll event listener to the document to resize the navbar
 * based on the scroll direction.
 */
document.addEventListener('scroll', debounce(resizeNavbarOnScroll, 10));

/**
 * Adds click event listeners to all anchor links with href starting with "#"
 * to scroll to the target element while accounting for the header height.
 */
document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
	anchor.addEventListener('click', scrollToElementWithHeaderOffset);
});

/**
 * Adds a click event listener to the cookie disclaimer button to set the cookie
 * acceptance in local storage and hide the disclaimer overlay.
 */
document
	.getElementById('cookie-disclaimer-btn')
	.addEventListener('click', acceptCookieDisclaimer);
